import { HttpClient } from '@angular/common/http';
import { isPlatformWorkerApp, Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { BehaviorSubject, merge, Observable } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  tap,
} from 'rxjs/operators';
import {
  AdditionalInformation,
  Application,
  BusinessInformation,
  CDDInformation,
  LoanApplication,
  PersonalInformation,
  Consent,
  ProductDetail,
  CustomerDetail,
  ILoanOffers,
  ILoanProduct,
  IFlow,
  FLOW_STATE,
} from './core/interfaces';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AppStateService {
  private readonly applicationState = new BehaviorSubject<Application>(
    {} as Application
  );
  readonly applicationState$ = this.applicationState.pipe(
    distinctUntilChanged()
  );

  private snapshotLoanApplication!: LoanApplication;
  readonly loanApplication$ = this.applicationState$.pipe(
    this.select(
      (state) => state.loanApplication,
      (loanApplicationState) =>
        (this.snapshotLoanApplication = loanApplicationState)
    )
  );

  private snapshotContentState!: Consent;
  readonly consent$ = this.applicationState$.pipe(
    this.select(
      (state) => state.consent,
      (contentState) => (this.snapshotContentState = contentState)
    )
  );
  private snapshotPersonalInformation!: PersonalInformation;
  readonly personalInformation$ = this.applicationState$.pipe(
    this.select(
      (state) => state.personalInformation,
      (personalInformationState) =>
        (this.snapshotPersonalInformation = personalInformationState)
    )
  );
  private snapshotBusinessInformation!: BusinessInformation;
  readonly businessInformation$ = this.applicationState$.pipe(
    this.select(
      (state) => state.businessInformation,
      (businessInformationState) =>
        (this.snapshotBusinessInformation = businessInformationState)
    )
  );
  private snapshotAdditionalInformation!: AdditionalInformation;
  readonly additionalInformation$ = this.applicationState$.pipe(
    this.select(
      (state) => state.additionalInformation,
      (additionalInformationState) =>
        (this.snapshotAdditionalInformation = additionalInformationState)
    )
  );

  private snapshotCDDInformation!: CDDInformation;
  readonly CDDInformation$ = this.applicationState$.pipe(
    this.select(
      (state) => state.CDDInformation,
      (CDDInformationState) =>
        (this.snapshotCDDInformation = CDDInformationState)
    )
  );

  private snapshotProductDetail!: ProductDetail;
  readonly ProductDetail$ = this.applicationState$.pipe(
    this.select(
      (state) => state.productDetail,
      (ProductDetail) => (this.snapshotProductDetail = ProductDetail)
    )
  );

  private snapshotCustomerDetail!: CustomerDetail;
  readonly CustomerDetail$ = this.applicationState$.pipe(
    this.select(
      (state) => state.customerDetail,
      (CustomerDetail) => (this.snapshotCustomerDetail = CustomerDetail)
    )
  );

  private snapshotLoanOffers!: ILoanOffers;
  readonly LoanOffers$ = this.applicationState$.pipe(
    this.select(
      (state) => state.loanOffers,
      (loanOffers) => (this.snapshotLoanOffers = loanOffers)
    )
  );

  private snapshotLoanProduct!: ILoanProduct;
  readonly LoanProduct$ = this.applicationState$.pipe(
    this.select(
      (state) => state.loanProduct,
      (loanProduct) => (this.snapshotLoanProduct = loanProduct)
    )
  );

  private snapshotFlow!: IFlow;
  readonly Flow$ = this.applicationState$.pipe(
    this.select(
      (state) => state.flow,
      (flow) => (this.snapshotFlow = flow)
    )
  );

  get snapshot() {
    return {
      application: this.applicationState.value,
      loanApplication: this.snapshotLoanApplication,
      personalInformation: this.snapshotPersonalInformation,
      businessInformation: this.snapshotBusinessInformation,
      additionalInformation: this.snapshotAdditionalInformation,
      CDDInformation: this.snapshotCDDInformation,
      consent: this.snapshotContentState,
      productDetail: this.snapshotProductDetail,
      customerDetail: this.snapshotCustomerDetail,
      loanOffers: this.snapshotLoanOffers,
      loanProduct: this.snapshotLoanProduct,
      flow: this.snapshotFlow,
    };
  }

  private readonly subscription = this.makeSubscription(
    this.applicationState$,
    this.loanApplication$,
    this.personalInformation$,
    this.businessInformation$,
    this.additionalInformation$,
    this.CDDInformation$,
    this.ProductDetail$,
    this.CustomerDetail$,
    this.LoanOffers$,
    this.LoanProduct$,
    this.Flow$
  );

  previousUrl = '';
  currentUrl = this.location.path();

  constructor(private location: Location, private router: Router) {
    this.location.onUrlChange((url) => {
      this.previousUrl = this.currentUrl;
      this.currentUrl = url;
    });
  }

  /**
   * internal only
   */
  private reduceState(fn: (prevState: Application) => Application) {
    const prevState = this.applicationState.value;
    const currState = fn(prevState);
    this.applicationState.next({ ...currState });
  }

  /**
   * internal only
   * @param selector
   * @param effect
   * @returns
   */
  private select<T, R>(
    selector: (state: T) => R,
    effect?: (selectState: R) => void
  ) {
    return (source: Observable<T>) =>
      source.pipe(
        map((state) => selector(state)),
        filter((selectState) => Boolean(selectState)),
        distinctUntilChanged(),
        tap((selectState) => effect && effect(selectState)),
        shareReplay(1)
      );
  }
  /**
   * internal only
   * @param srcs
   * @returns
   */
  private makeSubscription(...srcs: Observable<any>[]) {
    return merge(...srcs).subscribe();
  }

  /**
   * disconnect all state stream
   */
  disconnect() {
    this.subscription.unsubscribe();
  }

  updateStateLoanApplication(loanApplication: LoanApplication) {
    this.reduceState((prevState) => {
      prevState.loanApplication = loanApplication;
      return prevState;
    });
  }

  updateStatePersonalInformation(personalInformation: PersonalInformation) {
    this.reduceState((prevState) => {
      prevState.personalInformation = personalInformation;
      return prevState;
    });
  }

  updateStateBusinessInformation(businessInformation: BusinessInformation) {
    this.reduceState((prevState) => {
      prevState.businessInformation = businessInformation;
      return prevState;
    });
  }

  updateStateAdditionalInformation(
    additionalInformation: AdditionalInformation
  ) {
    this.reduceState((prevState) => {
      prevState.additionalInformation = additionalInformation;
      return prevState;
    });
  }

  updateStateCDDInformation(CDDInformation: CDDInformation) {
    this.reduceState((prevState) => {
      prevState.CDDInformation = CDDInformation;
      return prevState;
    });
  }

  updateStateConsent(consent: Consent) {
    this.reduceState((prevState) => {
      prevState.consent = consent;
      return prevState;
    });
  }

  updateStateProductDetail(productDetail: ProductDetail) {
    this.reduceState((prevState) => {
      prevState.productDetail = productDetail;
      return prevState;
    });
  }

  updateStateCustomerDetail(customerDetail: CustomerDetail) {
    this.reduceState((prevState) => {
      prevState.customerDetail = customerDetail;
      return prevState;
    });
  }

  updateStateLoanOffers(loanOffers: ILoanOffers) {
    this.reduceState((prevState) => {
      prevState.loanOffers = loanOffers;
      return prevState;
    });
  }

  updateStateLoanProduct(loanProduct: ILoanProduct) {
    this.reduceState((prevState) => {
      prevState.loanProduct = loanProduct;
      return prevState;
    });
  }

  updateStateFlow(flow: IFlow) {
    this.reduceState((prevState) => {
      if (!flow.enterFlow) {
        flow.enterFlow = prevState.flow.enterFlow;
      }
      prevState.flow = flow;
      return prevState;
    });
  }
}
